/**
 * Custom error class for API-related errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public endpoint?: string
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Retry a failed API call with exponential backoff
 * @param fn - The async function to retry
 * @param retries - Number of retry attempts (default: 3)
 * @param delay - Initial delay in milliseconds (default: 1000)
 * @returns Promise resolving to the function result
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    // Deterministic client errors (400, 401, 403, 404, etc.) will NEVER succeed on retry.
    // Immediately abort retry on 4xx to prevent 7+ seconds of blocking UI freezes.
    const statusCode =
      error instanceof ApiError
        ? error.statusCode
        : (error as any)?.statusCode || (error as any)?.status;

    const isClientError =
      (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500) ||
      (error instanceof Error && /HTTP\s*4\d\d/i.test(error.message));

    if (retries <= 0 || isClientError) {
      throw error;
    }
    
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryWithBackoff(fn, retries - 1, delay * 2);
  }
}
